import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, AlertTriangle, Loader2, Phone, CheckCircle2, RefreshCw } from 'lucide-react';

/**
 * AdminPhoneFixer — admin-only tool to clean up users whose phone numbers
 * would be rejected by the Twilio pre-validator (placeholder prefixes,
 * test ranges, repeated digits, bad length).
 *
 * Flow:
 *   1. GET /admin/users/with-bad-phones — list of offenders with reasons
 *   2. Admin types a replacement → PATCH /admin/users/{id}/phone
 *   3. Server validates + auto-formats to E.164 + enforces unique constraint
 *   4. On success, row disappears from the list
 *
 * Rationale: the SMS broadcast path (correctly) skips these users, so broadcasts
 * silently undercount. This tool is the only way to unblock them without
 * reaching into the DB.
 */
const AdminPhoneFixer = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({});  // user_id -> input string
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/users/with-bad-phones?limit=500');
      setRows(Array.isArray(data?.users) ? data.users : []);
      setTotal(data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load bad-phone users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const save = async (user) => {
    const draft = (drafts[user.id] || '').trim();
    if (!draft) { toast.error('Enter a phone number first'); return; }
    setSavingId(user.id);
    try {
      const { data } = await api.patch(`/admin/users/${user.id}/phone`, { phone: draft });
      toast.success(`${user.email}${data.phone ? ' → ' + data.phone : ''}`);
      // Drop the fixed row locally — no need to refetch the whole list.
      setRows(prev => prev.filter(r => r.id !== user.id));
      setTotal(t => Math.max(0, t - 1));
      setDrafts(prev => { const n = { ...prev }; delete n[user.id]; return n; });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const filtered = roleFilter === 'all' ? rows : rows.filter(r => r.role === roleFilter);
  const countByRole = rows.reduce((acc, r) => ({ ...acc, [r.role]: (acc[r.role] || 0) + 1 }), {});

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-6" data-testid="admin-phone-fixer">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="phone-fixer-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>

        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2">
          <Phone className="w-6 h-6 text-amber-600" /> Fix Phone Numbers
        </h1>
        <p className="text-sm text-slate-500 mb-5">
          Users here will be <strong>skipped by SMS broadcasts</strong> because their phone is a placeholder,
          test-range number, or malformed. Replace with a real mobile (we'll E.164-format it and check uniqueness).
        </p>

        {!loading && (
          <div className="flex items-center justify-between mb-3 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-50 text-amber-800 border border-amber-200">
                <AlertTriangle className="w-3 h-3 mr-0.5" /> {total} offenders
              </Badge>
              {['admin', 'client', 'talent', 'agency'].map(r => (
                countByRole[r] > 0 && (
                  <button
                    key={r}
                    onClick={() => setRoleFilter(roleFilter === r ? 'all' : r)}
                    className={`px-2 py-0.5 rounded border text-xs ${roleFilter === r ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                    data-testid={`phone-fixer-filter-${r}`}
                  >
                    {r} · {countByRole[r]}
                  </button>
                )
              ))}
              {roleFilter !== 'all' && (
                <button
                  onClick={() => setRoleFilter('all')}
                  className="text-slate-400 hover:text-slate-600"
                  data-testid="phone-fixer-filter-reset"
                >
                  clear
                </button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={fetchList} data-testid="phone-fixer-refresh">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        )}

        <Card>
          <CardHeader><CardTitle className="text-base">Users with unusable phones</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-500 flex flex-col items-center gap-2">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                All clear — no users with unusable phone numbers in this view.
              </div>
            ) : (
              <div className="divide-y">
                {filtered.map(u => (
                  <div key={u.id} className="py-3 grid gap-2 md:grid-cols-[1fr_auto] md:items-center" data-testid={`phone-fixer-row-${u.id}`}>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm">{u.full_name}</p>
                        <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-200 capitalize">{u.role}</Badge>
                        <Badge className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200">
                          {u.reason.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {u.email} · current: <code className="text-amber-700">{u.current_phone || '—'}</code>
                      </p>
                    </div>
                    <div className="flex gap-2 md:justify-end">
                      <Input
                        placeholder="+1 404 555 0123"
                        value={drafts[u.id] || ''}
                        onChange={(e) => setDrafts(prev => ({ ...prev, [u.id]: e.target.value }))}
                        className="w-56 text-sm"
                        data-testid={`phone-fixer-input-${u.id}`}
                        onKeyDown={(e) => { if (e.key === 'Enter') save(u); }}
                      />
                      <Button
                        size="sm"
                        onClick={() => save(u)}
                        disabled={savingId === u.id || !(drafts[u.id] || '').trim()}
                        data-testid={`phone-fixer-save-${u.id}`}
                      >
                        {savingId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
          <strong>Validation rules:</strong> must be 10–15 digits in E.164 form (e.g. +14042783571). Rejected:
          numbers starting with 000, 555-test ranges, or containing only repeated digits. Phones must be unique across users.
        </p>
      </div>
    </div>
  );
};

export default AdminPhoneFixer;
